import { useEffect, useState } from 'react';
import { getHistoricalPriceAt, COIN_MAP } from '../utils/coingecko';
import { INCOME_TYPES } from '../utils/transactionTypes';
import { isStablecoin } from '../utils/stablecoinResolver';

// Fiat-Gebühren brauchen keinen Marktpreis (der Betrag IST schon der EUR-Wert).
const FIAT_SET = new Set(['EUR', 'USD', 'GBP', 'CHF', 'AUD', 'CAD', 'JPY', 'TRY', 'BRL']);

/**
 * Lädt historische Tages-Schlusspreise für TXs, die einen Marktwert brauchen
 * aber kein `preis`-Feld haben (typisch: externe Auszahlungen ohne EUR-Erlös).
 *
 * Liefert eine Map<txId, eurValue> — der Display-Layer kann daraus den
 * abgeleiteten Gesamtwert ziehen, ohne dass tx.preis in der DB manipuliert wird.
 * (Invariante 9: Portfolio-State bleibt aus TX-Historie reproduzierbar.)
 *
 * @param {Array} txs            Transaktionsliste
 * @param {Object} options
 * @param {string} options.currency  Default 'eur'
 * @returns {Map<string, number>}    txId → abgeleiteter Marktwert (menge * historicalPrice)
 */
export function useHistoricalPrices(txs, { currency = 'eur' } = {}) {
  const [derivedValues, setDerivedValues] = useState(() => new Map());

  useEffect(() => {
    if (!Array.isArray(txs) || txs.length === 0) return;
    let cancelled = false;

    // Kandidaten: ALLE Transfers ohne EUR-Erlös (extern wie intern/eigene Wallet)
    // + eigenständige Coin-Gebühren (typ='gebuehr') + Income-Typen (Staking,
    // Airdrop, Mining, Interest, …), jeweils preis===0, sauberes Datum + coinId.
    // Fiat-Coins (EUR/USD…) ausgenommen — deren Betrag IST bereits der EUR-Wert.
    // Frühere Beschränkung auf `externalTransfer` führte dazu, dass bei einem
    // gematchten Transfer-Paar (z.B. Ledger → Binance) nur EINE Seite einen Wert
    // bekam und die Gegenseite „0,00 €" zeigte. Beide Beine eines Transfers haben
    // denselben wohldefinierten EUR-Gegenwert (menge × historischer Tagespreis).
    // Income (z.B. HBAR-Staking-Rewards) hat preis=0 ab Import → ohne Ableitung
    // dauerhaft „0,00 €", obwohl es ein realer (wenn auch winziger) Zufluss ist.
    // Rein Display: füttert displayValueEur, mutiert NIE tx.preis/Bestand.
    // Kandidaten → Einträge mit { tx, coinId, sym, datum, valueMenge }. valueMenge
    // ist die zu bepreisende Menge: bei Nicht-Swaps tx.menge, bei Swaps die Menge
    // der BEKANNTEN Seite. So bleibt der Wert = valueMenge × Tagespreis universell.
    const entries = [];
    for (const tx of txs) {
      if (Number(tx.preis) > 0) continue;
      if (!tx.datum || typeof tx.datum !== 'string') continue;
      const datum = tx.datum.slice(0, 10);

      // ── Swap: bekannte, nicht-Fiat/Stablecoin-Seite bepreisen (OUT bevorzugt) ──
      // Fiat-/Stablecoin-Seiten deckt resolveSwapEurValue/Display schon exakt ab.
      // Füllt z.B. „SOL → NAKMAK" (0 €) mit SOL-Menge × SOL-Tagespreis.
      if (tx.typ === 'swap') {
        const pick = (sym, menge) => {
          const up = String(sym || '').toUpperCase();
          if (!up || FIAT_SET.has(up) || isStablecoin(up)) return null;
          const id = COIN_MAP[up]?.id;
          const m = Number(menge);
          return (id && m > 0) ? { coinId: id, sym: up, valueMenge: m } : null;
        };
        const side = pick(tx.coinOut, tx.mengeOut) || pick(tx.coinIn, tx.mengeIn);
        if (side) entries.push({ tx, datum, ...side });
        continue;
      }

      // ── Transfers / Gebühren / Income (wie bisher) ───────────────────────────
      const isTransfer = tx.typ === 'einzahlung' || tx.typ === 'auszahlung';
      const isFee      = tx.typ === 'gebuehr';
      const isIncome   = INCOME_TYPES.has(tx.typ);
      if (!isTransfer && !isFee && !isIncome) continue;
      if (FIAT_SET.has(String(tx.coin || '').toUpperCase())) continue;
      if (!tx.coinId) continue;
      const m = Number(tx.menge);
      if (!Number.isFinite(m) || m <= 0) continue;
      entries.push({ tx, coinId: tx.coinId, sym: tx.coin, datum, valueMenge: m });
    }

    if (entries.length === 0) return;

    // Pro (coinId, datum) nur eine Anfrage — Gruppe sammelt die Einträge.
    const groups = new Map();
    for (const e of entries) {
      const key = `${e.coinId}__${e.datum}`;
      // sym (Coin-Symbol) für den CryptoCompare-Fallback im Proxy (>365 Tage).
      if (!groups.has(key)) groups.set(key, { coinId: e.coinId, sym: e.sym, datum: e.datum, items: [] });
      groups.get(key).items.push(e);
    }

    (async () => {
      let next = new Map(derivedValues);
      let changed = false;
      let pendingFlush = 0;
      for (const { coinId, sym, datum, items } of groups.values()) {
        const t0 = Date.now();
        const unit = await getHistoricalPriceAt(coinId, datum, currency, sym);
        if (cancelled) return;
        const elapsed = Date.now() - t0;
        if (typeof unit === 'number' && unit > 0) {
          for (const e of items) {
            const id = e.tx._docId || String(e.tx.id);
            const value = e.valueMenge * unit;
            if (next.get(id) !== value) { next.set(id, value); changed = true; pendingFlush++; }
          }
        }
        // Werte progressiv anzeigen statt erst am Schluss (bei vielen alten TXs
        // dauert der ganze Lauf sonst lange, ohne dass etwas erscheint).
        if (pendingFlush >= 8) { setDerivedValues(new Map(next)); pendingFlush = 0; }
        // Sanfte Drossel gegen CryptoCompare-Rate-Limit (Free-Tier ohne Key) —
        // NUR nach echten Netzwerk-Calls. Gecachte Tage (mem/localStorage/CDN)
        // liefert getHistoricalPriceAt quasi sofort → kein Throttle, kein Stau.
        if (elapsed > 25) await new Promise(r => setTimeout(r, 110));
      }
      if (changed && !cancelled) setDerivedValues(new Map(next));
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [txs, currency]);

  return derivedValues;
}
